// Hideo - local profiles: the list in localStorage (with this browser's active profile) and the storage key of each
// profile's data. The main profile keeps the original names, so data saved before profiles existed stays valid.
import { t } from '../i18n'
import type { Profile, ProfilesState } from './types'
import { isWebImg } from '../utils/url'

export const DEF_PROFILE = 'p_main'
const PROFILES_KEY = 'creatorOS_profiles'   // never rename
/** localStorage key of the save state of every profile (SYNC in src/stores/sync.ts) */
export const SYNC_KEY = 'creatorOS_sync'   // never rename
export const SOCIALS = ['youtube', 'instagram', 'tiktok', 'twitch'] as const
/** The social link fields of the profile and creator dialogs: key, label (a brand name, not translated), placeholder */
export const SOCIAL_FIELDS: readonly (readonly [(typeof SOCIALS)[number], string, string])[] = [
  ['youtube', 'YOUTUBE', 'https://youtube.com/@...'],
  ['instagram', 'INSTAGRAM', 'https://instagram.com/...'],
  ['tiktok', 'TIKTOK', 'https://tiktok.com/@...'],
  ['twitch', 'TWITCH', 'https://twitch.tv/...']
]
/** Profile ids end up in key and file names: only safe ids are accepted */
export const validProfileId = (id: unknown): id is string => typeof id === 'string' && /^p_[\w-]{1,40}$/.test(id)

// Profile read from the browser, the folder or a file: missing fields → defaults. The photo is a data URL or, for a
// linked image the site doesn't let the app copy (ImageBox.vue), an http(s) address; anything else is dropped
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function cleanProfile(x: any): Profile {
  const p = {
    id: x.id, name: String(x.name || t('Profile')).slice(0, 40), bio: String(x.bio || ''), updatedAt: +x.updatedAt || 0,
    avatar: typeof x.avatar === 'string' && (/^data:image\//.test(x.avatar) || isWebImg(x.avatar)) ? x.avatar.trim() : ''
  } as Profile
  SOCIALS.forEach(k => { p[k] = String(x[k] || '') })
  return p
}

/** localStorage key of a profile's data */
export const lsKey = (id: string) => (id === DEF_PROFILE ? 'creatorOS_db' : 'creatorOS_db_' + id)
/** File names in the legacy data folder */
const FNAME = 'creatoros-data.json'
export const PFNAME = 'creatoros-profiles.json'
export const dataFile = (id: string) => (id === DEF_PROFILE ? FNAME : `creatoros-data-${id}.json`)
export const backupFile = (id: string, day: string) => (id === DEF_PROFILE ? `creatoros-${day}.json` : `creatoros-${id}-${day}.json`)

export function loadProfiles(): ProfilesState {
  let p: ProfilesState | null = null
  try { p = JSON.parse(localStorage.getItem(PROFILES_KEY) || 'null') } catch { /* unreadable: start empty */ }
  if (!p || !Array.isArray(p.list)) p = { list: [], current: DEF_PROFILE }
  p.list = p.list.filter(x => x && validProfileId(x.id)).map(cleanProfile)
  // Whoever had data before profiles existed finds it in the main profile; a new installation starts with no profiles
  // (at start-up the profile picker only shows the "+")
  let legacy = false
  try { legacy = !!localStorage.getItem('creatorOS_db') } catch { /* no storage */ }
  if (!p.list.length && legacy) p.list = [cleanProfile({ id: DEF_PROFILE, name: t('My profile') })]
  if (!p.list.some(x => x.id === p!.current)) p.current = p.list.length ? p.list[0].id : ''
  return p
}

export function saveProfilesLocal(p: ProfilesState): void {
  try { localStorage.setItem(PROFILES_KEY, JSON.stringify(p)) } catch { /* storage full or blocked */ }
}

/** A profile's data as stored in this browser, or null */
export function loadLocal(id: string): unknown {
  try {
    const txt = localStorage.getItem(lsKey(id))
    if (txt) return JSON.parse(txt)
  } catch { /* unreadable */ }
  return null
}
/** Writes a profile's data to this browser; throws if the storage is full */
export function writeLocal(id: string, data: unknown): void {
  localStorage.setItem(lsKey(id), JSON.stringify(data))
}
